import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import updateMediaElementByUuidOperation from 'editor/src/store/design/operation/updateMediaElementByUuidOperation';
import getDesignData from 'editor/src/store/design/selector/getDesignData';
import applyAutoFillOperation from 'editor/src/store/editor/operation/applyAutoFillOperation';
import getLayoutSchemasBySlotNumbers from 'editor/src/store/editor/selector/getLayoutSchemasBySlotNumbers';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import getGalleryImages from 'editor/src/store/gallery/selector/getGalleryImages';
import { dismissPdfSplitAction, setPdfSplitUnplacedPagesAction } from 'editor/src/store/gallery/slice';
import { ImageState, PdfSplitProgress } from 'editor/src/store/gallery/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import getPdfPagePlacement from 'editor/src/util/autofill/getPdfPagePlacement';
import applyFillImageToElementPlaceholder from 'editor/src/util/design/applyFillImageToElementPlaceholder';

const fillPdfPagesOperation = (split: PdfSplitProgress) => (dispatch: ThunkDispatch, getState: () => RootState) => {
  const state = getState();
  const designData = getDesignData(state);
  const pageImageIds = split.pageImageIds ?? [];
  const imagesById = new Map(
    getGalleryImages(state)
      .filter((image) => image.state === ImageState.UPLOADED)
      .map((image) => [image.id, image]),
  );
  const targets = (designData ? getPdfPagePlacement(designData, pageImageIds).targets : []).map((target) => {
    const imageId = pageImageIds[target.pdfPage - 1];
    return { ...target, imageId, image: imageId ? imagesById.get(imageId) : undefined };
  });
  const pages = targets.flatMap(({ image, ...target }) => (image ? [{ ...target, image }] : []));
  // A page saved by the host but not usable from the gallery is still loading, failed to load, or was deleted.
  const unplacedPdfPages = targets.filter(({ imageId, image }) => imageId && !image).map(({ pdfPage }) => pdfPage);

  if (pages.length) {
    // the one-image layout autofill uses
    const layoutSchema = getLayoutSchemasBySlotNumbers(state, [1], true)[1];
    batch(() => {
      dispatch(saveUndoRedoStateOperation('Auto fill'));
      pages.forEach(({ placeholder, image }) => {
        if (placeholder?.width && placeholder.height) {
          dispatch(
            updateMediaElementByUuidOperation(placeholder.uuid, applyFillImageToElementPlaceholder(placeholder, image)),
          );
        }
      });
      dispatch(
        applyAutoFillOperation(
          pages
            .filter(({ placeholder }) => !placeholder)
            .map(({ spreadIndex, pageIndex, image }) => ({ spreadIndex, pageIndex, layoutSchema, images: [image] })),
        ),
      );
    });
  }
  // The notice stays while pages are left out, to say so and to let Fill place them once they are in the gallery.
  if (unplacedPdfPages.length) {
    dispatch(setPdfSplitUnplacedPagesAction({ uploaderId: split.uploaderId, pdfPages: unplacedPdfPages }));
  } else {
    dispatch(dismissPdfSplitAction(split.uploaderId));
  }
};

export default fillPdfPagesOperation;
